import {
  ComparatorContext,
  IProduct,
  getRendererFromDescriptor,
} from '@kizi/comparator-core';
import { Div, List, ListItem, P } from '@kizi/core-components';
import {
  Accordion,
  AccordionItem,
  TabDefinition,
  TabView,
} from '@kizi/shared-components';
import React, { MouseEventHandler, useContext } from 'react';
import { ProductNewProps, TabContentProps } from '../';
import { PriceMode } from '../../../utils/product';
import { ListComp, MoreInfoButton } from '../../Product/ExpandedPart';
import {
  accordionItemLabel,
  getCharacteristics,
} from '../../Product/ExpandedPart/functions';
import { ProductProsCons } from '../../ProductProsCons';
import { ProductUsps } from '../../ProductUsps';
import { DetailsTab } from './tabs/DetailsTab';
import { ReviewsTab } from './tabs/ReviewsTab';
import { SwitchTab } from './tabs/SwitchTab';
import { TariffsTab } from './tabs/TariffsTab';
interface ExpandedPartProps
  extends Omit<
    ProductNewProps,
    'priceMode' | 'nextIndex' | 'comparatorName' | 'onCompareChange'
  > {
  onTabChange: (i: any) => void;
  priceMode: PriceMode;
  setModalTab0: MouseEventHandler;
  setModalTab1: MouseEventHandler;
  setModalTab2: MouseEventHandler;
  setModalTab3: MouseEventHandler;
  selectedTabIndex: number;
  startIndex: number;
  purchasableProduct: boolean;
  tariffsTab?: React.ReactNode;
  tabsContent?: TabContentProps[];
  hideTestoordeel: boolean;
  getTabs?: any;
  hideProductUsps?: boolean;
}

export const ExpandedPart: React.FC<ExpandedPartProps> = ({
  data,
  priceMode,
  onTabChange,
  selectedTabIndex,
  setModalTab0,
  setModalTab1,
  setModalTab2,
  setModalTab3,
  startIndex,
  tariffsTab,
  tabsContent = [],
  hideTestoordeel,
  getTabs,
  hideProductUsps = false,
}: ExpandedPartProps) => {
  const { usps, purchasableProduct } = data;
  const { name } = useContext(ComparatorContext);
  const variant =
    !purchasableProduct && name.startsWith('os-') ? 'grayedContainer' : '';

  const tabs = getTabs
    ? getTabs({
        data,
        priceMode,
        setModalTab0,
        setModalTab1,
        setModalTab2,
        setModalTab3,
      })
    : getDefaultTabs(
        data,
        priceMode,
        setModalTab0,
        setModalTab1,
        setModalTab2,
        setModalTab3,
        { tariffsTab, tabsContent, name, hideTestoordeel },
      );

  return (
    <Div
      display="flex"
      px={[3, null, 5]}
      variant={variant}
      flexDirection="column"
      className="expandedPart"
    >
      <Div className="mobile" display={[null, null, 'none']}>
        {!hideProductUsps && (
          <Div>
            <ProductUsps
              data={usps}
              onItemClick={setModalTab0}
              showWarnings={false}
            />
          </Div>
        )}
        <Div>
          <Accordion
            allowOneItemOpen={true}
            items={getExpandedData(tabs)}
            variant="accordionProductMobile"
            selectedTabIndex={selectedTabIndex || undefined}
            startIndex={startIndex}
          />
        </Div>
      </Div>
      <Div className="desktop" display={['none', null, 'initial']}>
        <Div className="separator" />
        <TabView
          display="flex"
          flexDirection="column"
          selectedTabIndex={
            selectedTabIndex === 0 ? undefined : selectedTabIndex
          }
          startIndex={startIndex}
          tabs={tabs}
          tabProps={(selected) => ({
            variant: `tabViewTab${selected ? 'Selected' : ''}`,
          })}
          onTabChange={onTabChange}
        >
          {(Tabs, Content) => {
            return (
              <>
                <Div variant="tabViewTabsContainer">{Tabs}</Div>
                <Div>{Content}</Div>
              </>
            );
          }}
        </TabView>
      </Div>
    </Div>
  );
};

function getAboutTitle(comparatorType) {
  let title;
  switch (comparatorType) {
    case 'energy':
    case 'energyAuction':
      title = 'Over de leverancier';
      break;
    case 'home':
    case 'car':
    case 'health':
      title = 'Over de verzekeraar';
      break;
    case 'savings':
      title = 'Over de bank';
      break;
    case 'tripleplay':
      title = 'Over de provider';
      break;
    default:
      title = 'Onbekend type';
      break;
  }
  return title;
}

export function getDefaultTabs(
  data: IProduct,
  priceMode: ProductNewProps['priceMode'],
  setModalTab0: MouseEventHandler<Element>,
  setModalTab1: MouseEventHandler<Element>,
  setModalTab2: MouseEventHandler<Element>,
  setModalTab3: MouseEventHandler<Element>,
  tabsCustomContent,
): TabDefinition[] {
  const { characteristics, prosAndCons, provider, purchasableProduct, type } =
    data;
  const isArrayOfProsAndCons = Array.isArray(prosAndCons);
  const _prosAndCons = isArrayOfProsAndCons ? prosAndCons[0] : prosAndCons;
  const { attributes, description } = provider;
  const isOsHome = ['os-home'].includes(
    tabsCustomContent?.name,
  );

  const { hideTestoordeel } = tabsCustomContent || {};

  let tabTitles = [
    'Over verzekeraar',
    'Vergoedingen',
    'Premieopbouw',
    'Beoordelingen',
  ];

  if (type === 'energyAuction') {
    tabTitles = [
      'Over leverancier',
      'Productdetails',
      'Tarieven',
      'Beoordelingen',
    ];
  }
  let tabs: TabDefinition[] = tabTitles.map((tabTitle) => {
    return {
      label: tabTitle,
      content: <div />,
    };
  });

  if (purchasableProduct) {
    tabs.push({ label: 'Over je aanvraag', content: <div /> });
  }

  if (tabs.length === 5) {
    tabs[4].content = <SwitchTab data={data} isOs={isOsHome} />;
  }

  const title = getAboutTitle(type);

  tabs[0].content = (
    <>
      <Div
        display={[null, null, 'flex']}
        flexDirection={['column', 'column', 'row']}
      >
        <Div>
          <List variant="accordionProductInner">
            <ListItem>
              <Div variant="detailsListHeading">
                <P variant="bodyImportant">{title} </P>
              </Div>
              <p>{description}</p>
            </ListItem>
            <ListItem>
              <ProductProsCons {..._prosAndCons} />
            </ListItem>
          </List>
        </Div>
        <ListComp
          groups={[
            {
              rightFieldStyle: {
                textAlign: 'left',
              },
              list: characteristics || getCharacteristics(attributes),
              title: 'Kenmerken',
            },
          ]}
        />
      </Div>
      {type.includes('energy') && (
        <MoreInfoButton
          onClick={setModalTab0}
          text="Alles over deze leverancier"
        />
      )}
    </>
  );

  tabs[1].content = <DetailsTab data={data} onButtonClick={setModalTab1} />;

  tabs[2].content = (
    <>
      {!!tabsCustomContent?.tariffsTab?.length && (
        <>{tabsCustomContent?.tariffsTab}</>
      )}

      <TariffsTab
        data={data}
        onButtonClick={setModalTab2}
        priceMode={priceMode}
      />
    </>
  );

  tabs[3] = {
    label: 'Beoordelingen',
    content: (
      <ReviewsTab
        data={data}
        onButtonClick={setModalTab3}
        hideTestoordeel={hideTestoordeel}
      />
    ),
  };

  if (tabs.length === 5) {
    tabs[4].content = <SwitchTab data={data} isOs={isOsHome} />;
  }

  if (type === 'health') {
    return tabsCustomContent.tabsContent.map(({ label, content }) => ({
      label,
      content: getRendererFromDescriptor({
        type: content.type,
        props: {
          data,
        },
      }),
    }));
  }

  if (type === 'energyAuction') {
    tabs = tabs.filter((item) => item.label !== 'Beoordelingen');
  }

  return tabs;
}

function getExpandedData(tabs: TabDefinition[]): AccordionItem[] {
  return tabs.map(({ content, label }) => {
    return {
      label: accordionItemLabel(label as string),
      content,
    } as AccordionItem;
  });
}

export { CostTab, OsCostTab } from './tabs/CostTab';
export { DetailsTab } from './tabs/DetailsTab';
export { ReviewsTab, SolarPanelsReviewsTab } from './tabs/ReviewsTab';
export {
  HowItWorks,
  SwitchTab,
  SwitchTab3P,
  SwitchTabHome,
  SwitchTabOS3P,
} from './tabs/SwitchTab';
export { TariffsTab } from './tabs/TariffsTab';
